import { useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import { DEFAULT_APP_LOCALE, SUPPORTED_APP_LOCALES, getLocaleName } from './locales';
import { splitCurriculumHeadings } from './curriculumSearch';

export const SUPPORTED_SEO_LOCALES = SUPPORTED_APP_LOCALES.filter((entry) => entry.seoEnabled).map((entry) => entry.locale);
export const DEFAULT_SEO_LOCALE = DEFAULT_APP_LOCALE;
export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://schoolflix.web.app').replace(/\/+$/, '');

const SEO_GRADE_MIN = 8;
const SEO_GRADE_MAX = 12;

const SUBJECT_VISUALS = {
  Mathematics: {
    image: '/images/maths.jpg',
    accent: '#dc2626',
    focus: 'Build problem-solving confidence with topic explainers, worked examples, and quick revision reels.',
  },
  'Physical Sciences': {
    image: '/images/physicalscience.jpg',
    accent: '#ef4444',
    focus: 'Combine concept summaries, how-to guides, and fast revision support for CAPS-aligned science learning.',
  },
  'Physics (Physical Sciences)': {
    image: '/images/physicalscience.jpg',
    accent: '#ef4444',
    focus: 'Strengthen your Physical Sciences foundations with visual explanations, reel-based revision, and tutor support.',
  },
  'Life Sciences': {
    image: '/images/lifescience.jpg',
    accent: '#f87171',
    focus: 'Learn core Life Sciences topics through structured pages, study support, and short-form revision content.',
  },
  Geography: {
    image: '/images/geography.jpg',
    accent: '#b91c1c',
    focus: 'Use topic hubs and problem-solving guides to revise Geography in the order CAPS students actually need.',
  },
  History: {
    image: '/images/historys.jpg',
    accent: '#7f1d1d',
    focus: 'Move from topic summary to explanation to study support without leaving the SchoolFlix learning flow.',
  },
  'Business Studies': {
    image: '/images/art.jpg',
    accent: '#991b1b',
    focus: 'Turn Business Studies topics into searchable study pages with fast explanations and tutor-backed support.',
  },
  Accounting: {
    image: '/images/art.jpg',
    accent: '#dc2626',
    focus: 'Study Accounting through structured topic pages, worked examples, and revision reels designed for CAPS learners.',
  },
  'English HL': {
    image: '/images/english.jpg',
    accent: '#dc2626',
    focus: 'Support language and literature learning with clear summaries, how-to guides, and revision prompts.',
  },
  'Afrikaans FAL': {
    image: '/images/afrikaans.jpg',
    accent: '#ef4444',
    focus: 'Give Afrikaans students a clearer route from topic discovery to revision support and AI guidance.',
  },
};

function slugify(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toTitleCase(value) {
  return String(value || '')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function splitTopics(rawHeadings) {
  return splitCurriculumHeadings(rawHeadings);
}

function createTopicDescription({ topic, subject, grade }) {
  return `${topic} is part of the CAPS Grade ${grade} ${subject} journey. Use this SchoolFlix page to understand the concept, review worked examples, and move into reels, AI tutor support, and First Aid Kit study help.`;
}

function createHowToTitle(topic, grade, subject) {
  return `How to solve ${topic} in Grade ${grade} ${subject}`;
}

function createHowToDescription({ topic, subject, grade }) {
  return `Learn how to solve ${topic} for CAPS Grade ${grade} ${subject} with a clear method, worked example, common mistakes, and guided SchoolFlix follow-up support.`;
}

function createReelDescription({ topic, subject, grade, index }) {
  const durations = ['45s', '55s', '60s', '35s', '50s', '40s'];
  return {
    id: `${slugify(subject)}-${grade}-${slugify(topic)}-reel-${index + 1}`,
    title: `${topic} revision reel`,
    duration: durations[index % durations.length],
    summary: `A short-form SchoolFlix revision reel for ${topic} in Grade ${grade} ${subject}.`,
  };
}

function createFirstAidPrompts({ topic, subject, grade }) {
  return [
    `Explain ${topic} simply for a Grade ${grade} ${subject} student in South Africa.`,
    `Give me 5 quick practice questions for ${topic} in CAPS Grade ${grade} ${subject}.`,
    `What are the most common mistakes learners make in ${topic}?`,
  ];
}

function createWorkedExample(topic) {
  return [
    `Start by identifying what the question is really testing inside ${topic}.`,
    `Break the problem into the smallest steps before calculating or explaining anything.`,
    `Check your final answer against the key idea from the topic so the method stays clear.`,
  ];
}

function createCommonMistakes(topic) {
  return [
    `Skipping the core definition behind ${topic} and jumping straight to an answer.`,
    `Missing units, labels, or reasoning when explaining ${topic}.`,
    `Not checking whether the final result actually matches the concept being tested.`,
  ];
}

function createSupportChecklist(topic) {
  return [
    `Watch a quick SchoolFlix reel for ${topic}.`,
    `Read the worked example once without rushing.`,
    `Ask the AI tutor one focused question about ${topic}.`,
    `Use the First Aid Kit flow to practice weak spots.`,
  ];
}

function toSubjectRecord(row) {
  const grade = String(row.Grade).trim();
  const subject = String(row.Subject).trim();
  const phase = String(row.Phase || '').trim();
  const topics = splitTopics(row.Headings);
  const subjectSlug = slugify(subject);
  const visual = SUBJECT_VISUALS[subject] || {
    image: '/images/technology.jpg',
    accent: '#dc2626',
    focus: 'Study this CAPS subject with structured topic pages, quick reels, and AI-powered support.',
  };

  const topicRecords = topics.map((topic, index) => {
    const topicSlug = slugify(topic);
    const howToSlug = `${topicSlug}-grade-${grade}`;

    return {
      id: `${grade}-${subjectSlug}-${topicSlug}`,
      index,
      topic,
      topicSlug,
      howToSlug,
      description: createTopicDescription({ topic, subject, grade }),
      workedExample: createWorkedExample(topic),
      commonMistakes: createCommonMistakes(topic),
      supportChecklist: createSupportChecklist(topic),
      firstAidPrompts: createFirstAidPrompts({ topic, subject, grade }),
      reels: Array.from({ length: Math.min(3, Math.max(1, topic.length % 3 + 1)) }, (_, reelIndex) =>
        createReelDescription({ topic, subject, grade, index: reelIndex })
      ),
    };
  });

  return {
    key: `grade-${grade}/${subjectSlug}`,
    grade,
    phase,
    subject,
    subjectSlug,
    image: visual.image,
    accent: visual.accent,
    focus: visual.focus,
    overview: `Explore CAPS Grade ${grade} ${subject} through structured topic pages, fast revision reels, and AI-powered study support on SchoolFlix.`,
    topics: topicRecords,
  };
}

function buildCatalog(rows) {
  const filteredRows = rows
    .filter((row) => row.Subject && row.Grade)
    .filter((row) => {
      const grade = Number(row.Grade);
      return Number.isFinite(grade) && grade >= SEO_GRADE_MIN && grade <= SEO_GRADE_MAX;
    })
    .sort((left, right) => {
      const gradeDiff = Number(left.Grade) - Number(right.Grade);
      if (gradeDiff !== 0) {
        return gradeDiff;
      }
      return String(left.Subject).localeCompare(String(right.Subject));
    });

  const subjects = filteredRows.map(toSubjectRecord);
  const subjectsByKey = new Map(subjects.map((subject) => [subject.key, subject]));
  const topics = subjects.flatMap((subject) =>
    subject.topics.map((topic) => ({
      ...topic,
      grade: subject.grade,
      phase: subject.phase,
      subject: subject.subject,
      subjectSlug: subject.subjectSlug,
      subjectImage: subject.image,
      subjectAccent: subject.accent,
      subjectFocus: subject.focus,
      subjectOverview: subject.overview,
    }))
  );

  const topicsByKey = new Map(
    topics.map((topic) => [`grade-${topic.grade}/${topic.subjectSlug}/${topic.topicSlug}`, topic])
  );
  const howToBySlug = new Map(topics.map((topic) => [topic.howToSlug, topic]));

  return {
    subjects,
    subjectsByKey,
    topics,
    topicsByKey,
    howToBySlug,
  };
}

function parseCsvData(rawCsv) {
  const results = Papa.parse(rawCsv, {
    header: true,
    skipEmptyLines: true,
  });

  return Array.isArray(results.data) ? results.data : [];
}

export function isSupportedSeoLocale(locale) {
  return SUPPORTED_SEO_LOCALES.includes(locale);
}

export function getLocaleLabel(locale) {
  return getLocaleName(locale);
}

export function getSeoLocale(locale) {
  return isSupportedSeoLocale(locale) ? locale : DEFAULT_SEO_LOCALE;
}

export function buildAbsoluteUrl(pathname) {
  return `${SITE_URL}${pathname.startsWith('/') ? pathname : `/${pathname}`}`;
}

export function buildSubjectPath(locale, grade, subjectSlug) {
  return `/${getSeoLocale(locale)}/grade-${grade}/${subjectSlug}`;
}

export function buildTopicPath(locale, grade, subjectSlug, topicSlug) {
  return `${buildSubjectPath(locale, grade, subjectSlug)}/${topicSlug}`;
}

export function buildHowToPath(locale, howToSlug) {
  return `/${getSeoLocale(locale)}/how-to-solve/${howToSlug}`;
}

export function buildReelsHubPath(locale, grade, subjectSlug) {
  return `${buildSubjectPath(locale, grade, subjectSlug)}/revision-reels`;
}

export function buildFirstAidKitPath(locale, grade, subjectSlug, topicSlug) {
  return `/${getSeoLocale(locale)}/first-aid-kit/grade-${grade}/${subjectSlug}/${topicSlug}`;
}

export function createAlternatePaths(pathFactory) {
  return SUPPORTED_SEO_LOCALES.reduce((accumulator, locale) => {
    accumulator[locale] = pathFactory(locale);
    return accumulator;
  }, {});
}

export function useSeoCatalog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const cacheKey = 'seo_curriculum_catalog_v1';
    const cacheTimeKey = 'seo_curriculum_catalog_time_v1';
    const cacheTtlMs = 24 * 60 * 60 * 1000;

    const cachedCsv = sessionStorage.getItem(cacheKey);
    const cachedTime = Number(sessionStorage.getItem(cacheTimeKey) || 0);

    if (cachedCsv && Date.now() - cachedTime < cacheTtlMs) {
      setRows(parseCsvData(cachedCsv));
      setLoading(false);
      return undefined;
    }

    fetch('/CAPS_Subject___Grade___Headings.csv')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load curriculum data: ${response.status}`);
        }
        return response.text();
      })
      .then((rawCsv) => {
        if (!active) {
          return;
        }

        sessionStorage.setItem(cacheKey, rawCsv);
        sessionStorage.setItem(cacheTimeKey, Date.now().toString());
        setRows(parseCsvData(rawCsv));
        setLoading(false);
      })
      .catch((loadError) => {
        if (!active) {
          return;
        }
        console.error('SEO curriculum load error:', loadError);
        setError('Unable to load curriculum content right now.');
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const catalog = useMemo(() => buildCatalog(rows), [rows]);

  return {
    ...catalog,
    loading,
    error,
  };
}

export function getSubjectByParams(catalog, grade, subjectSlug) {
  return catalog.subjectsByKey.get(`grade-${grade}/${subjectSlug}`) || null;
}

export function getTopicByParams(catalog, grade, subjectSlug, topicSlug) {
  return catalog.topicsByKey.get(`grade-${grade}/${subjectSlug}/${topicSlug}`) || null;
}

export function getTopicByHowToSlug(catalog, howToSlug) {
  return catalog.howToBySlug.get(howToSlug) || null;
}

export function buildBreadcrumbs(items) {
  return items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: buildAbsoluteUrl(item.path),
  }));
}

export function createCourseJsonLd(subject, locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: `Grade ${subject.grade} ${subject.subject}`,
    description: subject.overview,
    provider: {
      '@type': 'Organization',
      name: 'SchoolFlix',
      url: buildAbsoluteUrl(buildSubjectPath(locale, subject.grade, subject.subjectSlug)),
    },
  };
}

export function createHowToJsonLd(topic, locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: createHowToTitle(topic.topic, topic.grade, topic.subject),
    description: createHowToDescription(topic),
    step: topic.workedExample.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      text: step,
    })),
    totalTime: 'PT5M',
    url: buildAbsoluteUrl(buildHowToPath(locale, topic.howToSlug)),
  };
}

export function createVideoJsonLd(subject, locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: `Grade ${subject.grade} ${subject.subject} revision reels`,
    description: `Short-form SchoolFlix revision reels for Grade ${subject.grade} ${subject.subject}.`,
    thumbnailUrl: buildAbsoluteUrl(subject.image),
    uploadDate: '2026-03-28',
    publisher: {
      '@type': 'Organization',
      name: 'SchoolFlix',
    },
    contentUrl: buildAbsoluteUrl(buildReelsHubPath(locale, subject.grade, subject.subjectSlug)),
  };
}

export function createFaqJsonLd(topic, locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `How should I start revising ${topic.topic}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Start with the topic summary, review the worked example, watch a SchoolFlix reel, and then use the AI tutor or First Aid Kit for targeted support.`,
        },
      },
      {
        '@type': 'Question',
        name: `What makes this page useful for Grade ${topic.grade} learners?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `It keeps the CAPS topic, the worked example, the revision reels, and the support tools in one linked study flow.`,
        },
      },
    ],
    url: buildAbsoluteUrl(buildFirstAidKitPath(locale, topic.grade, topic.subjectSlug, topic.topicSlug)),
  };
}

export function formatGradeLabel(grade) {
  return `Grade ${grade}`;
}

export function createSeoHeading(subject, locale) {
  if (locale === 'af') {
    return `${formatGradeLabel(subject.grade)} ${subject.subject} vir CAPS-leerders`;
  }

  return `${formatGradeLabel(subject.grade)} ${subject.subject} for CAPS learners`;
}

export function createHowToHeading(topic) {
  return createHowToTitle(topic.topic, topic.grade, topic.subject);
}

export function createFirstAidHeading(topic) {
  return `${formatGradeLabel(topic.grade)} ${topic.subject} First Aid Kit for ${topic.topic}`;
}

export function getSubjectSummary(subject) {
  return `${subject.overview} ${subject.focus}`;
}

export function getTopicSummary(topic) {
  return `${topic.description} This page links directly into revision reels, AI tutor help, and First Aid Kit study support.`;
}

export function getHowToSummary(topic) {
  return createHowToDescription(topic);
}

export function getReelsSummary(subject) {
  return `Watch short SchoolFlix revision reels for Grade ${subject.grade} ${subject.subject}, then jump into topic pages and AI tutor support when you need more depth.`;
}

export function getFirstAidSummary(topic) {
  return `Get guided support for ${topic.topic} in Grade ${topic.grade} ${topic.subject} with common mistakes, revision prompts, and a direct route into the SchoolFlix First Aid Kit flow.`;
}

export function getTopicPreviewItems(subject, count = 6) {
  return subject.topics.slice(0, count);
}

export function getRelatedTopics(catalog, topic, count = 3) {
  const subject = getSubjectByParams(catalog, topic.grade, topic.subjectSlug);
  if (!subject) {
    return [];
  }

  return subject.topics.filter((entry) => entry.topicSlug !== topic.topicSlug).slice(0, count);
}

export function getLocaleAwareLabel(locale, fallback, translated) {
  return locale === DEFAULT_SEO_LOCALE ? fallback : translated || fallback;
}

export function getLocaleNameList() {
  return SUPPORTED_APP_LOCALES.reduce((accumulator, entry) => {
    accumulator[entry.locale] = entry.nativeName;
    return accumulator;
  }, {});
}

export function formatTopicTitle(topic) {
  return toTitleCase(topic.topic);
}
